import React, { useCallback, useEffect, useRef, useState } from "react";
import { Form, Input, Modal, message } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isoWeek from "dayjs/plugin/isoWeek";
import { addDoc, collection, doc, getDocs, updateDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { GreenNotify, RedNotify, useFirebaseUpload } from "../../helper/helper";
import { normalizeCoordinates } from "../../helper/coordinates";
import {
  clearDraft,
  draftSavedLabel,
  readDraft,
  stripItemFiles,
  useBeforeUnloadWarning,
  useDraftSaver,
} from "../../helper/formDrafts";
import { isAssignableInspector } from "../../helper/userDisplay";
import { validateChecklistItems } from "../checklists/checklistValidation";
import CreateTaskModal from "./CreateTaskModal";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);

// First Monday of next week, as "YYYY-MM-DD" in the given timezone — seeds a
// property's cadence so weeklyInspectionReset starts on the next local Monday.
const nextMondayInTz = (tz) =>
  dayjs()
    .tz(tz || "America/Denver")
    .startOf("isoWeek")
    .add(1, "week")
    .format("YYYY-MM-DD");

const statusOptions = ["pending", "in progress", "completed"];
const bathOptions = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const kitchenOptions = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const bedOptions = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const porchOptions = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];
const lawnOptions = ["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10"];

const PROPERTY_DRAFT_KEY = "property:new";

/**
 * Self-contained property create/edit modal. Owns the whole property form
 * controller (checklist builder, cover-photo upload, geocoding, checklist
 * templates, draft autosave) so any page can mount it without re-wiring the
 * ~30 props CreateTaskModal needs. Used by the properties dashboard AND the
 * property detail page — the single source of truth for editing a property.
 *
 * Props:
 *  - open          show/hide
 *  - editingTask   property doc ({ _id, ... }) to edit, or null to create
 *  - usersData     optional assignable-inspector list; fetched internally if omitted
 *  - onClose       called when the modal should close (cancel or after save)
 *  - onSaved       called after a successful create/update
 */
const PropertyFormModal = ({
  open,
  editingTask = null,
  usersData: usersDataProp,
  onClose,
  onSaved,
}) => {
  const [form] = Form.useForm();
  const [questionaire, setQuestionaire] = useState([]);
  const [coordinates, setCoordinates] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [isloading, setIsLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [saveTemplateModal, setSaveTemplateModal] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState("");
  const [usersDataInternal, setUsersDataInternal] = useState([]);
  const usersData = usersDataProp || usersDataInternal;

  const restorePendingRef = useRef(false); // true while the restore prompt is up
  const restoreCheckedRef = useRef(false); // only prompt once per open

  const { uploadFiles } = useFirebaseUpload();

  // ── Draft autosave (create flow only) ───────────────────────────────────
  const { saveSoon } = useDraftSaver(PROPERTY_DRAFT_KEY, {
    enabled: open && !editingTask,
    buildSnapshot: () => ({
      values: form.getFieldsValue(true),
      questionaire: stripItemFiles(questionaire),
      coordinates,
      imageUrl: fileList?.[0]?.url || null,
    }),
    isMeaningful: (s) =>
      (s.questionaire?.length || 0) > 0 ||
      !!s.coordinates ||
      !!s.imageUrl ||
      Object.values(s.values || {}).some(
        (v) => typeof v === "string" && v.trim() !== "",
      ),
  });

  const triggerDraftSave = useCallback(() => {
    if (restorePendingRef.current) return;
    saveSoon();
  }, [saveSoon]);

  useBeforeUnloadWarning(
    () => open && !editingTask && !!readDraft(PROPERTY_DRAFT_KEY),
  );

  useEffect(() => {
    triggerDraftSave();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questionaire, coordinates]);

  // Load checklist templates (and assignable users when the parent didn't
  // supply them) each time the modal opens.
  useEffect(() => {
    if (!open) return;
    fetchChecklistTemplates();
    if (!usersDataProp) fetchAllUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Populate for edit, or clear for a fresh create, whenever the modal opens or
  // the target changes.
  useEffect(() => {
    if (!open) return;
    if (editingTask) {
      populateForEdit(editingTask);
    } else {
      form.resetFields();
      setQuestionaire([]);
      setFileList([]);
      setCoordinates(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingTask]);

  // On opening the create modal, offer to restore a saved draft (once).
  useEffect(() => {
    if (!open || editingTask) {
      restoreCheckedRef.current = false;
      return;
    }
    if (restoreCheckedRef.current) return;
    restoreCheckedRef.current = true;

    const draft = readDraft(PROPERTY_DRAFT_KEY);
    if (!draft?.data) return;

    restorePendingRef.current = true;
    Modal.confirm({
      title: "Restore unfinished property?",
      content: `You have an unsaved property from ${draftSavedLabel(
        draft.savedAt,
      )}. Restore your entries?`,
      okText: "Restore",
      cancelText: "Start fresh",
      centered: true,
      onOk: () => {
        const data = draft.data || {};
        form.setFieldsValue(data.values || {});
        setQuestionaire(
          Array.isArray(data.questionaire) ? data.questionaire : [],
        );
        setCoordinates(data.coordinates || null);
        setFileList(
          data.imageUrl
            ? [
                {
                  uid: "-1",
                  name: "property-image",
                  status: "done",
                  url: data.imageUrl,
                },
              ]
            : [],
        );
        restorePendingRef.current = false;
      },
      onCancel: () => {
        clearDraft(PROPERTY_DRAFT_KEY);
        restorePendingRef.current = false;
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingTask]);

  const fetchAllUsers = async () => {
    try {
      const snapshot = await getDocs(collection(db, "users"));
      const users = snapshot.docs.map((d) => ({ _id: d.id, ...d.data() }));
      // Only assignable inspectors (finished mobile profile, not admins).
      setUsersDataInternal(users.filter(isAssignableInspector));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const fetchChecklistTemplates = async () => {
    try {
      const snapshot = await getDocs(collection(db, "checklistTemplates"));
      setTemplates(snapshot.docs.map((d) => ({ _id: d.id, ...d.data() })));
    } catch (error) {
      console.error("Error fetching checklist templates:", error);
    }
  };

  const populateForEdit = (task) => {
    setQuestionaire(task?.checklistTemplate || []);
    // Show the property's current image (display-only — no originFileObj, so
    // onFinish keeps it unless the user replaces it).
    const existingImage = task?.propertyImages?.[0];
    setFileList(
      existingImage
        ? [
            {
              uid: "-1",
              name: "current-property-image",
              status: "done",
              url: existingImage,
            },
          ]
        : [],
    );
    setCoordinates(normalizeCoordinates(task?.coordinates));
    form.setFieldsValue({
      ownerName: task?.ownerDetails?.ownerName || "",
      ownerContact: task?.ownerDetails?.contact || "",
      ownerEmail: task?.ownerDetails?.email || "",
      beds: task?.propertyObjects?.beds || undefined,
      bath: task?.propertyObjects?.bath || undefined,
      kitchen: task?.propertyObjects?.kitchen || undefined,
      porch: task?.propertyObjects?.porch || undefined,
      lawn: task?.propertyObjects?.lawn || undefined,
      propertyName: task?.propertyName || "",
      location: task?.location || "",
      propertyDescription: task?.propertyDescription || "",
      assignedInspectors: Array.isArray(task?.assignedInspectors)
        ? task.assignedInspectors
        : [],
      inspectionIntervalWeeks: task?.inspectionIntervalWeeks || 1,
      timezone: task?.timezone || "America/Denver",
    });
  };

  const resetState = () => {
    form.resetFields();
    setQuestionaire([]);
    setFileList([]);
    setCoordinates(null);
  };

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  // ── checklist builder handlers ──────────────────────────────────────────
  const handleAddQuestion = () => {
    setQuestionaire((prev) => [
      ...prev,
      {
        id: Date.now().toString(),
        area: "",
        question: "",
        image: null,
        note: "",
        checks: [{ label: "" }],
        resolutionSteps: [""],
      },
    ]);
  };

  const handleAddProblem = (qIndex) => {
    const updated = [...questionaire];
    updated[qIndex].checks.push({ label: "" });
    setQuestionaire(updated);
  };

  const handleAddResolutionStep = (qIndex) => {
    const updated = [...questionaire];
    if (!updated[qIndex].resolutionSteps) updated[qIndex].resolutionSteps = [];
    updated[qIndex].resolutionSteps.push("");
    setQuestionaire(updated);
  };

  const handleRemoveResolutionStep = (qIndex, sIndex) => {
    const updated = [...questionaire];
    updated[qIndex].resolutionSteps.splice(sIndex, 1);
    setQuestionaire(updated);
  };

  // Apply a saved checklist template (deep copy, fresh ids, no answers).
  const handleApplyTemplate = (templateId) => {
    if (!templateId) return;
    const template = templates.find((t) => t._id === templateId);
    if (!template) return;
    const copied = (template.items || []).map((item, index) => ({
      id: `${Date.now()}-${index}`,
      area: item.area || "",
      question: item.question || "",
      image: item.image || null,
      note: "",
      checks: (item.checks || []).map((p) => ({ label: p.label || "" })),
      resolutionSteps: item.resolutionSteps || [],
    }));
    setQuestionaire(copied);
    GreenNotify(`Applied "${template.name}" — edit items below as needed`);
  };

  const openSaveAsTemplate = () => {
    if (!questionaire || questionaire.length === 0) {
      RedNotify("Add at least one checklist item first");
      return;
    }
    const propName = form.getFieldValue("propertyName");
    setNewTemplateName(propName ? `${propName} checklist` : "");
    setSaveTemplateModal(true);
  };

  const handleSaveAsTemplate = async () => {
    const name = newTemplateName.trim();
    if (!name) {
      RedNotify("Please enter a name for the checklist");
      return;
    }
    const checklistError = validateChecklistItems(questionaire);
    if (checklistError) {
      RedNotify(checklistError);
      return;
    }
    try {
      setIsLoading(true);
      const items = [];
      for (const [i, q] of questionaire.entries()) {
        if (q.imageFile) {
          const uploaded = await uploadFiles([q.imageFile]);
          if (!uploaded?.[0]?.image) {
            throw new Error(`Image upload failed for item #${i + 1}`);
          }
          q.image = uploaded[0].image;
          delete q.imageFile;
        }
        items.push({
          id: `${Date.now()}-${i}`,
          area: q.area || "",
          question: q.question || "",
          image: q.image || null,
          checks: (q.checks || []).map((p) => ({ label: p.label || "" })),
          resolutionSteps: q.resolutionSteps || [],
          note: "",
        });
      }
      await addDoc(collection(db, "checklistTemplates"), {
        name,
        description: "",
        items,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      GreenNotify(`Saved "${name}" to Checklists`);
      setSaveTemplateModal(false);
      setNewTemplateName("");
      fetchChecklistTemplates();
    } catch (error) {
      RedNotify(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const uploadProps = {
    multiple: false,
    listType: "picture-card",
    fileList,
    // Upload on pick (not at save time) so the URL can be drafted and survives
    // a refresh/close. On failure keep the local File for save-time fallback.
    async onChange({ fileList: newFileList }) {
      const file = newFileList.slice(-1)[0];
      if (!file?.originFileObj) {
        setFileList(newFileList.slice(-1));
        return;
      }
      setFileList([{ uid: file.uid, name: file.name, status: "uploading" }]);
      try {
        const uploaded = await uploadFiles([file.originFileObj]);
        const url = uploaded?.[0]?.image;
        if (url) {
          setFileList([{ uid: file.uid, name: file.name, status: "done", url }]);
        } else {
          throw new Error("no url");
        }
      } catch (e) {
        setFileList([{ ...file, status: "done" }]);
        RedNotify("Photo upload failed — we'll retry when you save.");
      }
      triggerDraftSave();
    },
    beforeUpload: () => false,
    onRemove: () => {
      setFileList([]);
      triggerDraftSave();
      return true;
    },
  };

  const onFinish = async (values) => {
    setIsLoading(true);
    try {
      // Backstop: the modal's Save gate already checks this, but the checklist
      // isn't an antd field so re-assert it with the shared rule here.
      const checklistError = validateChecklistItems(questionaire);
      if (checklistError) {
        message.error(checklistError);
        return;
      }

      // Upload checklist item images.
      for (const [qIndex, q] of questionaire.entries()) {
        if (q.imageFile) {
          const uploaded = await uploadFiles([q.imageFile]);
          if (!uploaded?.[0]?.image) {
            throw new Error(
              `Checklist image upload failed for item #${qIndex + 1}`,
            );
          }
          q.image = uploaded[0].image;
          delete q.imageFile;
        }
      }

      // Resolve the main property image. It normally uploads on pick, so the
      // fileList entry already carries its url — only fall back to a save-time
      // upload if an on-pick upload didn't complete.
      const existingPropertyImage = editingTask?.propertyImages?.[0] || "";
      let propertyImage = fileList[0]?.url || existingPropertyImage;
      if (fileList.length > 0 && fileList[0]?.originFileObj) {
        const uploaded = await uploadFiles([fileList[0].originFileObj]);
        if (!uploaded?.[0]?.image) {
          throw new Error("Main property image upload failed");
        }
        propertyImage = uploaded[0].image;
      }

      // Build a clean checklist template (no answers carried forward).
      const checklistTemplate = (questionaire || []).map((q) => ({
        id: q.id || Date.now().toString(),
        area: q.area || "",
        question: q.question || "",
        image: q.image || null,
        checks: (q.checks || []).map((p) => ({ label: p.label || "" })),
        resolutionSteps: q.resolutionSteps || [],
        note: "",
      }));

      const formattedData = {
        propertyObjects: {
          beds: values.beds || "0",
          bath: values.bath || "0",
          kitchen: values.kitchen || "0",
          porch: values.porch || "0",
          lawn: values.lawn || "0",
        },
        propertyDescription: values.propertyDescription || "",
        location: values.location || "",
        coordinates: normalizeCoordinates(coordinates),
        ownerDetails: {
          email: values.ownerEmail || "",
          contact: values.ownerContact || "",
          ownerName: values.ownerName || "",
        },
        propertyName: values.propertyName || "",
        checklistTemplate,
        propertyImages: propertyImage ? [propertyImage] : [],
        assignedInspectors: Array.isArray(values.assignedInspectors)
          ? values.assignedInspectors
          : [],
        inspectionIntervalWeeks: values.inspectionIntervalWeeks || 1,
        timezone: values.timezone || "America/Denver",
      };

      if (editingTask?._id) {
        await handleUpdateTask(editingTask._id, formattedData);
      } else {
        await handleCreateTask(formattedData);
      }
    } catch (error) {
      console.error("Error saving property:", error);
      RedNotify(error?.message || "Property save failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateTask = async (data) => {
    try {
      await addDoc(collection(db, "properties"), {
        ...data,
        lastInspectionDate: null,
        // Seed the cadence clock to next Monday in the property's own timezone.
        nextInspectionDue: nextMondayInTz(data.timezone),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      GreenNotify("Property created successfully");
      clearDraft(PROPERTY_DRAFT_KEY); // saved for real — drop the local draft
      onSaved?.();
      handleClose();
    } catch (error) {
      RedNotify(error.message);
    }
  };

  const handleUpdateTask = async (taskId, data) => {
    try {
      await updateDoc(doc(db, "properties", taskId), {
        ...data,
        updatedAt: new Date().toISOString(),
      });
      GreenNotify("Property updated successfully");
      onSaved?.();
      handleClose();
    } catch (error) {
      RedNotify(error.message);
    }
  };

  return (
    <>
      <CreateTaskModal
        open={open}
        editingTask={editingTask}
        onCancel={handleClose}
        form={form}
        onValuesChange={triggerDraftSave}
        onFinish={onFinish}
        onFinishFailed={({ errorFields }) => {
          form.scrollToField(errorFields[0].name);
          RedNotify("Please fill all required fields correctly");
        }}
        statusOptions={statusOptions}
        bedOptions={bedOptions}
        bathOptions={bathOptions}
        kitchenOptions={kitchenOptions}
        porchOptions={porchOptions}
        lawnOptions={lawnOptions}
        usersData={usersData}
        uploadProps={uploadProps}
        fileList={fileList}
        questionaire={questionaire}
        setQuestionaire={setQuestionaire}
        coordinates={coordinates}
        setCoordinates={setCoordinates}
        handleAddQuestion={handleAddQuestion}
        handleAddProblem={handleAddProblem}
        handleAddResolutionStep={handleAddResolutionStep}
        handleRemoveResolutionStep={handleRemoveResolutionStep}
        templates={templates}
        onApplyTemplate={handleApplyTemplate}
        onSaveAsTemplate={openSaveAsTemplate}
        isloading={isloading}
        onCloseAndReset={handleClose}
      />

      <Modal
        title="Save checklist as a template"
        open={saveTemplateModal}
        onCancel={() => setSaveTemplateModal(false)}
        onOk={handleSaveAsTemplate}
        okText="Save to Checklists"
        confirmLoading={isloading}
        maskClosable={false}
      >
        <p style={{ marginTop: 0, color: "#6b6258" }}>
          This copies the checklist items currently in the form into a reusable
          template under the Checklists tab.
        </p>
        <Input
          placeholder="e.g. Standard 3BR Residential"
          maxLength={60}
          value={newTemplateName}
          onChange={(e) => setNewTemplateName(e.target.value)}
          onPressEnter={handleSaveAsTemplate}
        />
      </Modal>
    </>
  );
};

export default PropertyFormModal;
